import { format } from "date-fns";
import type { Locale } from "date-fns";
import { de, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  CalendarDays,
  Clock,
  ExternalLink,
  Image,
  Info,
  Link as LinkIcon,
  MessageSquare,
  User,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ProfileAvatar } from "@/components/common/ProfileAvatar";
import { cn } from "@/lib/utils";
import { getProfileDisplayName } from "@/lib/profileUtils";
import { daysUntilDue } from "@/lib/dateUtils";
import type { DefectCardData, DefectProfile, DefectCardProps } from "./DefectCard.types";

interface DefectCardDetailsProps extends Pick<
  DefectCardProps,
  "inspectionStatus" | "currentUserId" | "compact"
> {
  defect: DefectCardData;
  isOverdue: boolean;
}

export function DefectCardDetails(props: DefectCardDetailsProps) {
  const { defect, compact = false } = props;

  return (
    <>
      {defect.description && !compact && (
        <p className="text-muted-foreground break-words">{defect.description}</p>
      )}

      <CancellationNotice {...props} />
      <AwaitingVerificationNotice {...props} />
      <ContextRow defect={defect} />
      <MetaRow {...props} />
      <AssignedUserRow defect={defect} />
    </>
  );
}

function CancellationNotice({ defect, inspectionStatus }: DefectCardDetailsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (defect.status !== "cancelled" || !defect.inspection || inspectionStatus !== "cancelled") {
    return null;
  }

  return (
    <Alert variant="default" className="py-2 bg-muted/50 border-muted-foreground/20">
      <Info className="h-4 w-4" />
      <AlertDescription className="text-xs flex items-center justify-between gap-2 flex-wrap">
        <span>{t("defects.cancelledWithInspectionInfo")}</span>
        <Button
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/inspections/${defect.inspection!.id}`);
          }}
        >
          <ExternalLink className="h-3 w-3 mr-1" />
          {t("inspections.goToInspection")}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

function AwaitingVerificationNotice({ defect, currentUserId }: DefectCardDetailsProps) {
  const { t } = useTranslation();
  const shouldShow = [
    defect.status === "resolved",
    Boolean(currentUserId),
    defect.assigned_to === currentUserId,
    defect.verifying_qm !== currentUserId,
  ].every(Boolean);

  if (!shouldShow) return null;

  return (
    <div className="flex items-center gap-2 rounded-lg border border-info/20 bg-info/10 px-3 py-2">
      <Clock className="h-4 w-4 flex-shrink-0 text-info" />
      <span className="text-sm text-info">
        {t("defects.awaitingVerificationInfo", {
          qmName: getAwaitingVerificationName(defect, t("defects.verifyingQm")),
        })}
      </span>
    </div>
  );
}

function ContextRow({ defect }: { defect: DefectCardData }) {
  if (!defect.inspection && !defect.checklist_item) return null;

  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2 flex-wrap">
      <LinkIcon className="h-4 w-4" />
      <InspectionLink defect={defect} />
      <ChecklistItemLabel defect={defect} />
    </div>
  );
}

function InspectionLink({ defect }: { defect: DefectCardData }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!defect.inspection) return null;

  return (
    <Button
      variant="link"
      size="sm"
      className="h-auto p-0 inline-flex items-center gap-1.5 text-muted-foreground hover:text-primary transition-colors group"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/inspections/${defect.inspection!.id}`);
      }}
    >
      {t("defects.inspection")}:
      <span className="font-medium text-foreground group-hover:text-primary group-hover:underline">
        {defect.inspection.checklists?.name}
        <InspectionVersion version={defect.inspection.checklists?.version} />
      </span>
      <ExternalLink className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
    </Button>
  );
}

function InspectionVersion({ version }: { version?: string | null }) {
  if (!version) return null;

  return <span className="text-muted-foreground font-normal ml-1">(v{version})</span>;
}

function ChecklistItemLabel({ defect }: { defect: DefectCardData }) {
  const { t } = useTranslation();
  if (!defect.checklist_item) return null;

  return (
    <>
      {defect.inspection && <span>{"\u2022"}</span>}
      <span>
        {t("defects.checklistItem")}:{" "}
        <span className="font-medium text-foreground">{defect.checklist_item.title}</span>
      </span>
    </>
  );
}

function MetaRow({ defect, isOverdue }: DefectCardDetailsProps) {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? enUS : de;

  return (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
      <div className="flex items-center gap-2">
        <ProfileAvatar profile={defect.created_by_profile} size="sm" />
        <span data-sentry-mask>
          {t("defects.createdBy")} {getProfileName(defect.created_by_profile, t("common.unknown"))}
        </span>
      </div>
      <span>
        {format(new Date(defect.created_at), "d. MMM yyyy, HH:mm", { locale: dateLocale })}
      </span>
      <CommentsMeta defect={defect} />
      <DueDateMeta defect={defect} isOverdue={isOverdue} dateLocale={dateLocale} />
      <ImagesMeta defect={defect} />
    </div>
  );
}

function CommentsMeta({ defect }: { defect: DefectCardData }) {
  const { t } = useTranslation();
  if ((defect.comments_count ?? 0) <= 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      <MessageSquare className="h-4 w-4" />
      <span>
        {defect.comments_count} {t("defects.comment", { count: defect.comments_count })}
      </span>
    </div>
  );
}

// TES-2237 (US6, ehrliches Verlust-Framing): anticipatory window — a due date within this many
// days (but not yet overdue) gets the honest "fällig in X Tagen" framing ALONGSIDE the date, not
// instead of it. Mirrors the existing "Fällig (7 Tage)" convention used elsewhere (schedules KPI).
const ANTICIPATORY_DUE_WINDOW_DAYS = 7;

function DueDateMeta({
  defect,
  isOverdue,
  dateLocale,
}: {
  defect: DefectCardData;
  isOverdue: boolean;
  dateLocale: Locale;
}) {
  const { t } = useTranslation();
  if (!defect.due_date) return null;

  // Copilot review (CHK PR #860): `isOverdue` is false for closed/cancelled defects (they have no
  // active deadline — isDefectOverdue only evaluates open/in_progress/resolved), so gating on
  // `!isOverdue` alone let a closed/cancelled defect with a future due_date show "fällig in X
  // Tagen" as if it still had a live countdown. Gate on the SAME active-status set isDefectOverdue
  // uses, so a defect with no active deadline never gets anticipatory framing either.
  const hasActiveDeadline =
    defect.status === "open" || defect.status === "in_progress" || defect.status === "resolved";

  // Honest, NEUTRAL fact — never shown once overdue (that is the retrospective "Überfällig"
  // framing, styled destructive above) and never colored/urgent before the deadline (WARGAME
  // US6: "keine Dringlichkeits-Farbe vor Ablauf").
  const remaining = isOverdue || !hasActiveDeadline ? null : daysUntilDue(defect.due_date);
  const showAnticipatory =
    remaining !== null && remaining >= 0 && remaining <= ANTICIPATORY_DUE_WINDOW_DAYS;

  return (
    <div className={cn("flex items-center gap-1.5", isOverdue && "text-destructive")}>
      <CalendarDays className="h-4 w-4" />
      <span>
        {t("dashboard.dueDate")}:{" "}
        {format(new Date(defect.due_date), "dd. MMM yyyy", { locale: dateLocale })}
        {showAnticipatory && (
          <span className="ml-1 text-muted-foreground" data-testid="defect-due-anticipatory">
            (
            {remaining === 0
              ? t("dashboard.tester.dueToday")
              : t("defects.dueInDays", { count: remaining })}
            )
          </span>
        )}
      </span>
    </div>
  );
}

function ImagesMeta({ defect }: { defect: DefectCardData }) {
  const { t } = useTranslation();
  if (!defect.images || defect.images.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5">
      <Image className="h-4 w-4" />
      <span>
        {defect.images.length} {t("defects.image", { count: defect.images.length })}
      </span>
    </div>
  );
}

function AssignedUserRow({ defect }: { defect: DefectCardData }) {
  const { t } = useTranslation();
  if (!defect.assigned_to_profile) {
    return (
      <div className="flex items-center gap-2 mt-2 -mx-2 rounded-md bg-surface-container-low/60 px-2 py-1.5">
        <User className="h-4 w-4 text-warning" />
        <span className="text-sm text-warning font-medium">{t("dashboard.unassigned")}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 mt-2 -mx-2 rounded-md bg-surface-container-low/60 px-2 py-1.5">
      <ProfileAvatar profile={defect.assigned_to_profile} size="sm" />
      <span className="text-sm">
        {t("dashboard.assignedTo")}:{" "}
        <span className="font-medium" data-sentry-mask>
          {getProfileName(defect.assigned_to_profile, t("common.unknown"))}
        </span>
      </span>
    </div>
  );
}

function getProfileName(profile: DefectProfile | null | undefined, fallback: string): string {
  return getProfileDisplayName(profile, fallback);
}

function getAwaitingVerificationName(defect: DefectCardData, fallback: string): string {
  if (defect.verifying_qm_profile) {
    return getProfileDisplayName(defect.verifying_qm_profile);
  }

  if (defect.created_by_profile) {
    return getProfileDisplayName(defect.created_by_profile);
  }

  return fallback;
}
